import { TSubscriptionCurrencyCode } from "src/data/billing/types/billingTypes"

const ZERO_DECIMAL_CURRENCIES = new Set(["JPY"])

function currencyLocale(currency: string): Intl.UnicodeBCP47LocaleIdentifier {
  switch (currency.toLowerCase()) {
    case "eur":
    case "gbp":
      return "en-GB"
    case "sek":
      return "sv-SE"
    default:
      return "en-US"
  }
}

export function formatCentsInFullUnits(
  cents: number = 0,
  currency: TSubscriptionCurrencyCode,
  {
    locale,
    minimumFractionDigits,
  }: {
    locale?: Intl.UnicodeBCP47LocaleIdentifier
    minimumFractionDigits?: number
  } = {}
): string {
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase())
  const divisor = isZeroDecimal ? 1 : 100
  const converted = cents / divisor
  // Zero-decimal currencies (e.g. JPY) never show fractional digits.
  // Otherwise: use caller-supplied value, or default to 0 for whole amounts and 2 for non-whole.
  const minFrac = isZeroDecimal
    ? 0
    : (minimumFractionDigits ?? (converted % 1 === 0 ? 0 : 2))
  const maxFrac = isZeroDecimal ? 0 : Math.max(minFrac, 2)
  const resolvedLocale = locale ?? currencyLocale(currency)
  try {
    return new Intl.NumberFormat(resolvedLocale, {
      style: "currency",
      currency,
      minimumFractionDigits: minFrac,
      maximumFractionDigits: maxFrac,
    }).format(converted)
  } catch {
    return `${currency.toUpperCase()} ${converted}`
  }
}

// Converts an amount in full currency units (e.g. 10.5) into the lowest
// currency subunit the API expects (e.g. 1050 cents). Zero-decimal currencies
// (e.g. JPY) have no subunit, so the amount is passed through, rounded.
export function convertFullUnitsToCents(
  amount: number,
  currency: TSubscriptionCurrencyCode
): number {
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase())
  const multiplier = isZeroDecimal ? 1 : 100
  return Math.round(amount * multiplier)
}

// Inverse of convertFullUnitsToCents: converts a subunit amount (e.g. 1050
// cents) back into full currency units (e.g. 10.5). Zero-decimal currencies
// (e.g. JPY) have no subunit, so the amount is passed through unchanged.
export function convertCentsToFullUnits(
  cents: number,
  currency: TSubscriptionCurrencyCode
): number {
  const isZeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase())
  const divisor = isZeroDecimal ? 1 : 100
  return cents / divisor
}

// Formats a price already expressed in full currency units (not cents).
// Whole amounts drop the decimals; fractional amounts show two.
export function formatPrice(
  price: number,
  currency: TSubscriptionCurrencyCode
): string {
  const decimals = price % 1 === 0 ? 0 : 2
  try {
    return new Intl.NumberFormat(currencyLocale(currency), {
      style: "currency",
      currency,
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(price)
  } catch {
    return `${currency.toUpperCase()} ${price}`
  }
}
